import classNames from 'classnames'
import React, { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { selectHeaderHeight, selectMainPEPositionAtStart } from '../../store/ui/ui.selectors'
import { isGE } from '../Device'
import ResponsiveScrollableWrapper from '../genericComponents/ResponsiveScrollableWrapper/ResponsiveScrollableWrapper'
import { HEADER_SUBHEADER_HEIGHT, PREHEADER_HEIGHT } from '../header/header-constants'
import { MINI_MAP_HEIGHT, PE_MAIN_START_POS, PE_MAIN_VALUES } from './main.constants'
import s from './Main.less'
import Anchor from '../genericComponents/Anchor'
import { isSearchRoute, selectCurrentHistoryRoute } from '../../store/history/history.selectors'
import {
  selectSearchAppParameter,
  selectSearchSubCategoryParameter,
  selectSearchTerms
} from '../../store/search/search.selectors'
import { capitalize, replaceDashesBySpaces } from '../../utils/string'
import useMainScroll from '../ui/useMainScroll'
import useUIDisplay from '../ui/useUIDisplay'
import { UI_ELEMENTS } from '../../store/ui/ui.constants'
import { selectIsMapFullScreen } from '../../store/map/map.selectors'
import { resetUiDrawerClientTop } from '../../store/ui/uiSlice'

const getTopPosition = (positionAtStart, headerWithSubHeaderHeight) => {
  const windowHeight = global?.window?.innerHeight ?? 0
  const minTop = headerWithSubHeaderHeight + MINI_MAP_HEIGHT
  const { high, low_scrolled_high, low } = PE_MAIN_START_POS

  if (positionAtStart === high) return minTop

  const mainTopValue = positionAtStart === low_scrolled_high ? low : positionAtStart

  return Math.max(minTop, windowHeight - PE_MAIN_VALUES[mainTopValue])
}

const Main = ({ children, className, withoutSubHeader, hidden, preContent, preContentHeight = 0 }) => {
  const { mainRef, triggerInitialScroll } = useMainScroll()
  const headerHeight = useSelector(selectHeaderHeight)
  const positionAtStart = useSelector(selectMainPEPositionAtStart)
  const { isUIDisplayed: hasPopin } = useUIDisplay(UI_ELEMENTS.POPIN)
  const hasFullscreenMap = useSelector(selectIsMapFullScreen)
  const mainIsVisible = !hasPopin && !hasFullscreenMap
  const dispatch = useDispatch()

  useEffect(() => {
    if (mainIsVisible && positionAtStart === PE_MAIN_START_POS.low_scrolled_high) {
      setTimeout(() => triggerInitialScroll(), 0)
    }
  }, [positionAtStart, mainIsVisible])

  useEffect(() => {
    if (hasFullscreenMap) dispatch(resetUiDrawerClientTop())
  }, [hasFullscreenMap])

  const getStyle = useCallback(() => {
    if (__SERVER__) return {}

    const headerWithSubHeaderHeight = headerHeight + (withoutSubHeader ? 0 : HEADER_SUBHEADER_HEIGHT)

    if (isGE()) {
      const top = PREHEADER_HEIGHT + headerWithSubHeaderHeight
      const height = `calc(100vh - ${top + preContentHeight}px)`
      return { top, height }
    }

    return {
      top: getTopPosition(positionAtStart, headerWithSubHeaderHeight),
      height: null
    }
  }, [headerHeight, positionAtStart, withoutSubHeader, preContentHeight])

  return (
    <main
      className={classNames(s.Main, s.Main_displayed, className, hidden && s.Main_hidden)}
      ref={mainRef}
      style={getStyle()}
    >
      <Anchor />
      <div className={s.mainPrecontentWrapper}>
        <MainCategoryName />
        {preContent}
      </div>
      <ResponsiveScrollableWrapper>{children}</ResponsiveScrollableWrapper>
    </main>
  )
}

const MainCategoryName = () => {
  const currentRoute = useSelector(selectCurrentHistoryRoute)
  const appParameter = useSelector(selectSearchAppParameter)
  const subCategoryParameter = useSelector(selectSearchSubCategoryParameter)
  const searchTerms = useSelector(selectSearchTerms)

  if (__SERVER__ || isGE() || !isSearchRoute(currentRoute)) return <></>

  const getCategoryName = () => {
    if (appParameter || subCategoryParameter) return searchTerms
    if (searchTerms) return replaceDashesBySpaces(capitalize(searchTerms))
    return 'Résultats'
  }

  return (
    <div className={s.mainCategoryNameWrapper}>
      <span className={s.mainCategoryName}>{getCategoryName()}</span>
    </div>
  )
}

export default Main
